/**
 * Générateur — Connaitre le cube de 10.
 * Attendu ID : 928 | Niveau : 5ème | Domaine : Nombres et calculs / Puissances
 *
 * Plan de cartes (mise en conformité avec la spécification 928, campagne 5e,
 * 2026-09-22) :
 *
 *   ⚠️ Variabilité structurellement étroite assumée : l'attendu est UNE
 *   connaissance ponctuelle (10³ = 1 000). Le seuil des 20 empreintes est
 *   atteint par rotation des SENS de la question (direct, inverse en
 *   puissance, inverse en base) et des FAMILLES de distracteurs — jamais en
 *   gonflant les nombres, que la spec fixe à la base 10 et à l'exposant 3.
 *
 *   Carte « 10 au cube » (id dix)
 *     dix_texte_trou_saisie + dix_texte_trou_drag : UN item, d'un des trois
 *       sens de question (exclusion 7 : jamais deux fois le même couple
 *       base-sens, et la base est toujours 10). La consigne annonce la forme
 *       attendue (variables.format_de_reponse_annonce).
 *
 *   Carte « Vrai ou faux : le cube de 10 » (id vf)
 *     vf_tableau_vf : cinq affirmations sur neuf, deux ou trois vraies ; le
 *       carré de 10 n'apparaît que dans les affirmations FAUSSES.
 *     vf_qcm_unique : mode à réponses fermées. Au sens direct, les quatre
 *       propositions littérales de la spec (30, 100, 1 000, 10 000) ; aux deux
 *       sens inverses, quatre propositions dont une constante adossée à une
 *       erreur nommée et deux tirées d'un pool. Le gabarit précédent est exclu
 *       du tirage suivant : la même question ne sort jamais deux fois de suite
 *       (retour prof 2026-09-25).
 *
 *     ⚠️ VIVIER VOLONTAIREMENT ÉTROIT, ne pas chercher à l'élargir. L'objectif
 *       est de connaître LE cube de 10 (arbitrage prof 2026-09-25), et la spec
 *       fixe base, exposant et valeur attendue à 10, 3 et 1 000 — trois bornes
 *       `prescrit`. Les autres cubes ne servent que de distracteurs ou de
 *       variantes que l'enseignant active lui-même, jamais par défaut. La carte
 *       n'a donc que trois questions : au troisième exercice, l'élève en revoit
 *       forcément une. C'est le propre d'un automatisme isolé, pas un défaut.
 *
 * Spécification (C:\tanoed\Attendu-5e\specifications-tanoed\5e\928.json) :
 *   Bornes appliquées : base 10 exactement, exposant 3 exactement, valeur
 *   attendue 1 000 (les trois sont `prescrit`) ; une question par carte
 *   (variables.nombre_de_questions_par_feuille min 1, « valeur par défaut ») ;
 *   les trois sens de question ; la forme attendue annoncée dans la consigne.
 *   Exclusions appliquées : aucune base autre que 10 (les cubes de 2 et de 3
 *   du niveau 4 restent désactivés) ; aucun exposant autre que 3 dans une
 *   question — le carré n'apparaît que parmi les réponses fausses ; aucune
 *   conversion d'unités, et plus aucun habillage de volume ou d'aire
 *   (objets_geometriques est vide) ; jamais deux fois le même couple base-sens
 *   dans une même feuille.
 *   Erreurs visées → mots-clés :
 *     cube confondu avec le triple (30)        → cube_et_triple
 *     cube confondu avec le carré (100, 10²)   → cube_et_carre
 *     zéros comptés à côté (10 000)            → zeros_comptes_a_cote
 *     écriture rendue autre que celle demandée → ecriture_non_demandee
 *     exposant donné au lieu de la base (3)    → exposant_au_lieu_de_la_base
 *   Graduation → cartes : niveau 1 → vf_qcm_unique et dix_texte_trou_* au sens
 *   direct (quatre propositions littérales de la spec pour le QCM) ; niveau 2 →
 *   les mêmes cartes aux sens inverses ; niveau 3 → vf_tableau_vf, seule carte
 *   où les deux sens se mêlent dans une même question sans que l'un donne la
 *   réponse de l'autre ; niveau 4 → aucune carte, la spec le dit « désactivé
 *   par défaut ».
 *   Non tranché : `exclusions` 3 (« aucun exposant autre que 3 pour la question
 *   elle-même ») contredit le niveau 3 de `graduation_difficulte` (« avec le
 *   carré de 10 parmi les questions ») — l'exclusion est appliquée, le carré ne
 *   figure que dans les propositions fausses et les affirmations fausses du
 *   tableau. Aucune carte de saisie libre d'une puissance : une question à fait
 *   unique n'a qu'une seule empreinte et serait bloquée par le harnais ; le sens
 *   inverse passe par les trous et par le mode à réponses fermées.
 *
 * Le tableau double/carré/triple/cube est porté par gen_927 (décision
 * anti-doublon du plan initial), il n'est pas repris ici.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, filtrerDistracteurs,
} from '../../../utils';

const ATTENDU_ID = 928;

const L_DIX = '10 au cube';
const L_VF = 'Vrai ou faux : le cube de 10';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ═════════ Carte 1 — 10 au cube ═════════

/**
 * Un pool d'items par SENS de question (variables.sens_de_la_question) :
 * 1 = on donne 10³, l'élève écrit 1 000 ;
 * 2 = on donne 1 000, l'élève l'écrit comme une puissance de 10 ;
 * 3 = on donne 1 000, l'élève dit de quel nombre c'est le cube.
 *
 * UN SEUL item par carte : `intention_pedagogique` dit « une question, une
 * réponse, aucune étape intermédiaire », et `exclusions` 7 interdit de poser
 * deux fois le même couple base-sens — or la base est toujours 10, donc deux
 * items d'une même carte relèveraient forcément de deux sens différents, et
 * chacun donnerait la réponse de l'autre (un item du sens 1 écrit « 10 », qui
 * est la réponse du sens 3 ; un item du sens 2 écrit « 1000 », qui est la
 * réponse du sens 1). La variété vient du pool, pas de l'empilement.
 *
 * 1 000 est écrit SANS séparateur de milliers : la saisie doit correspondre.
 */
const ITEMS_PAR_SENS = {
    1: [
        '10³ = 10 × 10 × 10 = {{1000}}',
        '10³ = {{1000}}',
        'Le cube de 10 vaut {{1000}}',
        '10 × 10 × 10 = {{1000}}',
        'Le cube de 10 s\'écrit {{1000}} en chiffres',
        '10 × 10 × 10 est le {{cube}} de 10',
        'Multiplier 10 trois fois par lui-même donne {{1000}}',
        'Le résultat de 10 × 10 × 10 est {{1000}}',
    ],
    2: [
        '1000 = 10 à la puissance {{3}}',
        'Pour écrire 1000 comme une puissance de 10, l\'exposant vaut {{3}}',
        '1000 s\'écrit 10 avec l\'exposant {{3}}',
        '1000 est la puissance de 10 d\'exposant {{3}}',
        'Dans l\'écriture de 1000 en puissance de 10, l\'exposant est {{3}}',
        '1000 = 10 × 10 × 10, soit 10 à la puissance {{3}}',
        'L\'exposant qui fait passer de 10 à 1000 est {{3}}',
    ],
    3: [
        '1000 est le cube de {{10}}',
        'Le nombre dont le cube vaut 1000 est {{10}}',
        'Le nombre qui, multiplié trois fois par lui-même, donne 1000 est {{10}}',
        '1000 est le cube du nombre {{10}}',
        'Le nombre qui a pour cube 1000 est {{10}}',
        'La base de la puissance égale à 1000 est {{10}}',
    ],
};

/** Un item, d'un sens tiré parmi les trois (exclusion 7 : un seul couple base-sens). */
const tirerItemDix = () => {
    const sens = randomChoice([1, 2, 3]);
    return { sens, item: randomChoice(ITEMS_PAR_SENS[sens]) };
};

const genDixSaisie = () => {
    const { sens, item } = tirerItemDix();
    const payload = makeTexteTrou(item,
        '<p><b>Complète.</b> (Nombres en chiffres ; mots en toutes lettres.)</p>',
        { ...opts(L_DIX), mode: 'saisie' });
    payload._meta.params = {
        items: [item], sens: [sens], niveau: sens === 1 ? 1 : 2,
        erreurVisee: sens === 3
            ? ['exposant_au_lieu_de_la_base', 'ecriture_non_demandee']
            : ['ecriture_non_demandee'],
    };
    return payload;
};

const genDixDrag = () => {
    const { sens, item } = tirerItemDix();
    const distracteurs = filtrerDistracteurs(item, shuffle([
        '30',        // le triple
        '300',       // 3 × 100
        '20',        // le double
        '10000',     // un zéro de trop
        '100',       // le carré
        '3',         // l'exposant donné pour la base
        'carré',     // le mot jumeau (filtré s'il est attendu)
        'double',
    ])).slice(0, 4);
    const payload = makeTexteTrou(item,
        '<p><b>Complète avec la bonne étiquette.</b></p>',
        { ...opts(L_DIX), mode: 'drag', distracteurs });
    payload._meta.params = {
        items: [item], sens: [sens], niveau: sens === 1 ? 1 : 2,
        erreurVisee: ['cube_et_triple', 'cube_et_carre', 'zeros_comptes_a_cote'],
    };
    return payload;
};

// ═════════ Carte 2 — Vrai/Faux et réponses fermées ═════════

// Le carré de 10 ne paraît QUE dans les affirmations fausses (exclusions 3).
const AFFIRMATIONS_VRAIES = [
    '10³ = 1000',
    '1000 = 10 × 10 × 10',
    'Le cube de 10 est 1000',
    '1000 est le cube de 10',
    'Le nombre 1000 s\'écrit 10³',
];
const AFFIRMATIONS_FAUSSES = [
    '10³ = 30',                       // cube_et_triple
    '10³ = 10 × 3',                   // cube_et_triple, côté calcul
    '10² = 1000',                     // cube_et_carre
    '10³ = 100',                      // cube_et_carre, dans l'autre sens
    'Le carré de 10 est 1000',        // cube_et_carre, côté vocabulaire
    '10³ = 10000',                    // zeros_comptes_a_cote
    '1000 est le cube de 3',          // exposant_au_lieu_de_la_base
];

const genVfCube = () => {
    const nbVrai = randInt(2, 3);
    const vraies = shuffle(AFFIRMATIONS_VRAIES).slice(0, nbVrai);
    const fausses = shuffle(AFFIRMATIONS_FAUSSES).slice(0, 5 - nbVrai);
    const indexees = shuffle([
        ...vraies.map(l => ({ l, r: 0 })),
        ...fausses.map(l => ({ l, r: 1 })),
    ]);
    const payload = makeVraiFaux(
        indexees.map(x => x.l),
        indexees.map(x => x.r),
        '<p><b>Vrai ou faux ?</b> Pour chaque affirmation, coche la bonne case.</p>',
        opts(L_VF));
    payload._meta.params = {
        nbVrai, niveau: 3,
        erreurVisee: ['cube_et_triple', 'cube_et_carre', 'zeros_comptes_a_cote', 'exposant_au_lieu_de_la_base'],
    };
    return payload;
};

// Sens direct : les quatre propositions LITTÉRALES de la spec (type_reponse),
// nombre constant, chacune adossée à une erreur nommée.
const QUESTIONS_SENS_DIRECT = [
    'Quel est le cube de 10 ?',
    'Calcule : 10³',
    'Calcule : 10 × 10 × 10',
    'Combien vaut le cube de 10 ?',
];
const PROPOSITIONS_SENS_DIRECT = [
    { valeur: '1000', code: 'correct' },
    { valeur: '30', code: 'cube_et_triple' },
    { valeur: '100', code: 'cube_et_carre' },
    { valeur: '10000', code: 'zeros_comptes_a_cote' },
];

const QUESTIONS_SENS_PUISSANCE = [
    'Quelle est l\'écriture de 1000 comme une puissance de 10 ?',
    'Comment s\'écrit 1000 sous la forme d\'une puissance de 10 ?',
];
const POOL_SENS_PUISSANCE = [
    { valeur: '10²', code: 'cube_et_carre' },
    { valeur: '10 × 3', code: 'cube_et_triple' },
    { valeur: '10⁴', code: 'zeros_comptes_a_cote' },
    { valeur: '3¹⁰', code: 'base_et_exposant_echanges' },
];

const QUESTIONS_SENS_BASE = [
    'De quel nombre 1000 est-il le cube ?',
    '1000 est le cube de quel nombre ?',
];
const POOL_SENS_BASE = [
    { valeur: '30', code: 'cube_et_triple' },
    { valeur: '100', code: 'cube_et_carre' },
    { valeur: '1000', code: 'ecriture_non_demandee' },
    { valeur: '10000', code: 'zeros_comptes_a_cote' },
];

const GABARITS_QCM = [
    () => ({
        texte: `<p><b>${randomChoice(QUESTIONS_SENS_DIRECT)}</b> (Réponse en écriture décimale.)</p>`,
        correct: '1000',
        distracteurs: PROPOSITIONS_SENS_DIRECT.filter(p => p.code !== 'correct'),
        sens: 1, niveau: 1,
    }),
    () => ({
        texte: `<p><b>${randomChoice(QUESTIONS_SENS_PUISSANCE)}</b></p>`,
        correct: '10³',
        // « 1000 » est toujours proposé : c'est l'écriture qui n'était pas demandée.
        distracteurs: [
            { valeur: '1000', code: 'ecriture_non_demandee' },
            ...shuffle(POOL_SENS_PUISSANCE).slice(0, 2),
        ],
        sens: 2, niveau: 2,
    }),
    () => ({
        texte: `<p><b>${randomChoice(QUESTIONS_SENS_BASE)}</b></p>`,
        correct: '10',
        // « 3 » est toujours proposé : c'est l'exposant donné au lieu de la base.
        distracteurs: [
            { valeur: '3', code: 'exposant_au_lieu_de_la_base' },
            ...shuffle(POOL_SENS_BASE).slice(0, 2),
        ],
        sens: 3, niveau: 2,
    }),
];

/**
 * Mémoire courte du dernier sens tiré : la même question ne sort plus deux fois de
 * suite (retour prof 2026-09-25).
 *
 * Les trois gabarits posent trois questions différentes du même fait, 1000 = 10³ :
 * trouver le nombre, trouver son écriture en puissance, trouver sa base. Avec un
 * tirage libre, une question sur trois répétait la précédente, et deux fois la même
 * question à la suite se voit tout de suite.
 *
 * Huit essais au plus : la répétition ne survit pas à huit tirages, et la borne
 * garantit que la fonction se termine quoi qu'il arrive.
 */
const refSensQcm = { v: null };

/** Le sens que pose chaque gabarit, dans l'ordre de GABARITS_QCM. */
const SENS_DES_GABARITS = [1, 2, 3];

const genQcmCube = () => {
    // On EXCLUT le gabarit précédent au lieu de retirer au hasard jusqu'à tomber sur un
    // autre : la répétition devient impossible, là où une boucle bornée en laissait
    // passer une sur quelques milliers.
    const possibles = GABARITS_QCM.filter((_, i) => SENS_DES_GABARITS[i] !== refSensQcm.v);
    const tire = randomChoice(possibles)();
    refSensQcm.v = tire.sens;
    const { texte, correct, distracteurs, sens, niveau } = tire;
    const payload = makeQcm([correct, ...distracteurs.map(d => d.valeur)], correct, texte, opts(L_VF));
    payload._meta.params = { correct, sens, niveau, distracteurs };
    return payload;
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    dix_texte_trou_saisie: genDixSaisie,
    dix_texte_trou_drag: genDixDrag,
    vf_tableau_vf: genVfCube,
    vf_qcm_unique: genQcmCube,
};

const FORMAT_LABELS = {
    texte_trou_saisie: 'Saisie trous', texte_trou_drag: 'Drag',
    tableau_vf: 'Vrai/Faux', qcm_unique: 'QCM',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'dix', label: L_DIX, formatsList: buildFormats('dix') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
